import React, { useCallback, useEffect, useState } from 'react';
import Layout from '../../shared/components/Layout';
import { useAuth } from '../auth/AuthContext';
import { extractErrorMessage } from '../../shared/utils/errors';
import { formatCurrencyNetworkLabel } from '../../shared/utils/networkLabel';
import {
  WithdrawalCurrencyFee,
  WithdrawalCurrencyNetwork,
  WithdrawalFeeRule,
  WithdrawalSettings,
  WithdrawalTransferType,
} from '../../shared/types';
import { getWithdrawalSettings, updateWithdrawalSettings } from './api';

/**
 * Withdrawal configuration screen.
 *
 * Fees and daily limits are configured separately for the two transfer
 * types: external withdrawals (funds leave the platform on-chain) and
 * internal transfers (to another platform user's deposit address). Fees are
 * per currency and per type: a visible percentage of the coin amount, a
 * fixed USD fee, a USD floor for the visible fee, and a hidden percentage
 * silently deducted on top that the user never sees broken out. A currency
 * (or type) without a rule is free.
 */
type FeeInputs = { fee: string; fixed: string; min: string; hidden: string };
type CurrencyFeeInputs = Record<WithdrawalTransferType, FeeInputs>;

const EMPTY_FEE_INPUTS: FeeInputs = { fee: '', fixed: '', min: '', hidden: '' };
const EMPTY_CURRENCY_FEE_INPUTS: CurrencyFeeInputs = {
  external: { ...EMPTY_FEE_INPUTS },
  internal: { ...EMPTY_FEE_INPUTS },
};

const TRANSFER_TYPES: { type: WithdrawalTransferType; label: string; limitPrefix: string }[] = [
  { type: 'external', label: 'External withdrawal', limitPrefix: 'daily_limit_usd_level_' },
  { type: 'internal', label: 'Internal transfer', limitPrefix: 'internal_daily_limit_usd_level_' },
];

const LEVELS = [1, 2, 3, 4, 5] as const;

const LEVEL_LABELS: Record<number, string> = {
  1: 'Level 1 — registration',
  2: 'Level 2 — email verified',
  3: 'Level 3 — phone verified',
  4: 'Level 4 — basic KYC',
  5: 'Level 5 — advanced KYC',
};

const toFeeInputs = (rule: WithdrawalFeeRule | undefined): FeeInputs => ({
  fee: rule?.transaction_fee_percent != null ? String(rule.transaction_fee_percent) : '',
  fixed: rule?.transaction_fee_fixed_usd != null ? String(rule.transaction_fee_fixed_usd) : '',
  min: rule?.min_fee_usd != null ? String(rule.min_fee_usd) : '',
  hidden: rule?.hidden_fee_percent != null ? String(rule.hidden_fee_percent) : '',
});

const toCurrencyFeeInputs = (
  fees: WithdrawalSettings['currency_fees']
): Record<string, CurrencyFeeInputs> => {
  const inputs: Record<string, CurrencyFeeInputs> = {};
  if (!fees || Array.isArray(fees)) return inputs;
  Object.entries(fees).forEach(([currency, entry]) => {
    inputs[currency.toUpperCase()] = {
      external: toFeeInputs(entry?.external),
      internal: toFeeInputs(entry?.internal),
    };
  });
  return inputs;
};

const toLimitInputs = (settings: WithdrawalSettings): Record<string, string> => {
  const inputs: Record<string, string> = {};
  TRANSFER_TYPES.forEach(({ limitPrefix }) => {
    LEVELS.forEach((level) => {
      const key = `${limitPrefix}${level}`;
      const value = settings[key as keyof WithdrawalSettings];
      inputs[key] = value === null || value === undefined ? '' : String(value);
    });
  });
  return inputs;
};

const RatesPage: React.FC = () => {
  const { admin } = useAuth();

  const [settings, setSettings] = useState<WithdrawalSettings | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [limitInputs, setLimitInputs] = useState<Record<string, string>>({});
  const [reviewThresholdInput, setReviewThresholdInput] = useState<string>('');
  const [savingLimits, setSavingLimits] = useState(false);
  const [limitsBanner, setLimitsBanner] = useState<string | null>(null);
  const [poolPairs, setPoolPairs] = useState<WithdrawalCurrencyNetwork[]>([]);
  const [selectedPairs, setSelectedPairs] = useState<Set<string>>(new Set());
  const [savingPairs, setSavingPairs] = useState(false);
  const [pairsBanner, setPairsBanner] = useState<string | null>(null);
  const [currencyFeeInputs, setCurrencyFeeInputs] = useState<Record<string, CurrencyFeeInputs>>({});
  const [newFeeCurrency, setNewFeeCurrency] = useState<string>('');
  const [savingCurrencyFees, setSavingCurrencyFees] = useState(false);
  const [currencyFeesBanner, setCurrencyFeesBanner] = useState<string | null>(null);

  const pairKey = (currency: string, network: string) =>
    `${currency.toUpperCase()}:${network.toUpperCase()}`;

  const canManage =
    admin?.role === 'super_admin' ||
    admin?.backoffice_role === 'administrator' ||
    (admin?.permissions?.includes('withdrawal.manage_rates') ?? false);

  const load = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await getWithdrawalSettings();
      setSettings(data);
      setLimitInputs(toLimitInputs(data));
      setReviewThresholdInput(String(data.auto_review_threshold_usd));
      setPoolPairs(data.pool_withdrawal_pairs || data.allowed_withdrawal_options || []);
      setCurrencyFeeInputs(toCurrencyFeeInputs(data.currency_fees));
      setSelectedPairs(
        new Set(
          (data.allowed_withdrawal_options || []).map((p) => pairKey(p.currency, p.network))
        )
      );
    } catch (err) {
      setError(extractErrorMessage(err, 'Failed to load withdrawal settings'));
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const setLimitInput = (key: string, value: string) => {
    setLimitsBanner(null);
    setLimitInputs((prev) => ({ ...prev, [key]: value }));
  };

  const parseLimit = (value: string, label: string): number | null | 'invalid' => {
    if (value.trim() === '') {
      return null;
    }
    const num = Number(value);
    if (!Number.isFinite(num) || num < 0) {
      setError(`${label} must be a non-negative number or empty (unlimited for level 5 only)`);
      return 'invalid';
    }
    return num;
  };

  const handleSaveLimits = async (e: React.FormEvent) => {
    e.preventDefault();
    setLimitsBanner(null);
    setError(null);

    const payload: Partial<WithdrawalSettings> = {};
    for (const { label, limitPrefix } of TRANSFER_TYPES) {
      for (const level of LEVELS) {
        const key = `${limitPrefix}${level}` as keyof WithdrawalSettings;
        const parsed = parseLimit(limitInputs[key] ?? '', `${label} level ${level} limit`);
        if (parsed === 'invalid') return;
        (payload as Record<string, number | null>)[key] = level === 5 ? parsed : parsed ?? 0;
      }
    }

    const threshold = Number(reviewThresholdInput);
    if (!Number.isFinite(threshold) || threshold < 0) {
      setError('Manual review threshold must be a non-negative number');
      return;
    }
    payload.auto_review_threshold_usd = threshold;

    setSavingLimits(true);
    try {
      const updated = await updateWithdrawalSettings(payload);
      setSettings(updated);
      setLimitInputs(toLimitInputs(updated));
      setLimitsBanner('Limits saved');
    } catch (err) {
      setError(extractErrorMessage(err, 'Failed to save limits'));
    } finally {
      setSavingLimits(false);
    }
  };

  const togglePair = (currency: string, network: string) => {
    const key = pairKey(currency, network);
    setSelectedPairs((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const handleSavePairs = async (e: React.FormEvent) => {
    e.preventDefault();
    setPairsBanner(null);
    setError(null);

    if (selectedPairs.size === 0) {
      setError('Select at least one currency/network pair');
      return;
    }

    const allowed_withdrawal_options = Array.from(selectedPairs).map((key) => {
      const [currency, network] = key.split(':');
      return { currency, network };
    });

    setSavingPairs(true);
    try {
      const updated = await updateWithdrawalSettings({ allowed_withdrawal_options });
      setSettings(updated);
      setPairsBanner('Allowed networks saved');
    } catch (err) {
      setError(extractErrorMessage(err, 'Failed to save allowed networks'));
    } finally {
      setSavingPairs(false);
    }
  };

  const setCurrencyFeeField = (
    currency: string,
    type: WithdrawalTransferType,
    field: keyof FeeInputs,
    value: string
  ) => {
    setCurrencyFeesBanner(null);
    setCurrencyFeeInputs((prev) => {
      const entry = prev[currency] ?? EMPTY_CURRENCY_FEE_INPUTS;
      return {
        ...prev,
        [currency]: { ...entry, [type]: { ...entry[type], [field]: value } },
      };
    });
  };

  const parseFeeValue = (raw: string, max: number): number | null => {
    if (raw.trim() === '') return 0;
    const value = Number(raw);
    if (!Number.isFinite(value) || value < 0 || value > max) return null;
    return value;
  };

  const handleSaveCurrencyFees = async (e: React.FormEvent) => {
    e.preventDefault();
    setCurrencyFeesBanner(null);
    setError(null);

    const currency_fees: Record<string, WithdrawalCurrencyFee> = {};
    for (const [currency, entry] of Object.entries(currencyFeeInputs)) {
      const rules: WithdrawalCurrencyFee = {};
      for (const { type, label } of TRANSFER_TYPES) {
        const inputs = entry[type];
        const fee = parseFeeValue(inputs.fee, 50);
        if (fee === null) {
          setError(`${currency} (${label}): fee percent must be between 0 and 50`);
          return;
        }
        const fixed = parseFeeValue(inputs.fixed, 100000);
        if (fixed === null) {
          setError(`${currency} (${label}): fixed fee must be between 0 and 100,000`);
          return;
        }
        const min = parseFeeValue(inputs.min, 10000);
        if (min === null) {
          setError(`${currency} (${label}): min fee USD must be between 0 and 10,000`);
          return;
        }
        const hidden = parseFeeValue(inputs.hidden, 50);
        if (hidden === null) {
          setError(`${currency} (${label}): hidden fee must be between 0 and 50`);
          return;
        }
        rules[type] = {
          transaction_fee_percent: fee,
          transaction_fee_fixed_usd: fixed,
          min_fee_usd: min,
          hidden_fee_percent: hidden,
        };
      }
      currency_fees[currency] = rules;
    }

    setSavingCurrencyFees(true);
    try {
      const updated = await updateWithdrawalSettings({ currency_fees });
      setSettings(updated);
      setCurrencyFeeInputs(toCurrencyFeeInputs(updated.currency_fees));
      setCurrencyFeesBanner('Per-currency fees saved');
    } catch (err) {
      setError(extractErrorMessage(err, 'Failed to save per-currency fees'));
    } finally {
      setSavingCurrencyFees(false);
    }
  };

  const addFeeCurrency = () => {
    const currency = newFeeCurrency.toUpperCase();
    if (!currency || currencyFeeInputs[currency]) return;
    setCurrencyFeesBanner(null);
    setCurrencyFeeInputs((prev) => ({
      ...prev,
      [currency]: { external: { ...EMPTY_FEE_INPUTS }, internal: { ...EMPTY_FEE_INPUTS } },
    }));
    setNewFeeCurrency('');
  };

  const removeFeeCurrency = (currency: string) => {
    setCurrencyFeesBanner(null);
    setCurrencyFeeInputs((prev) => {
      const next = { ...prev };
      delete next[currency];
      return next;
    });
  };

  const feeCurrencies = Object.keys(currencyFeeInputs).sort();
  const addableFeeCurrencies = Array.from(
    new Set(poolPairs.map((pair) => pair.currency.toUpperCase()))
  )
    .filter((currency) => !currencyFeeInputs[currency])
    .sort();
  const allowedWithoutFees = Array.from(
    new Set(
      (settings?.allowed_withdrawal_options ?? []).map((pair) => pair.currency.toUpperCase())
    )
  )
    .filter((currency) => !currencyFeeInputs[currency])
    .sort();

  const formatTimestamp = (iso: string | null | undefined) =>
    iso ? new Date(iso).toLocaleString() : '—';

  const feeInput = (
    currency: string,
    type: WithdrawalTransferType,
    field: keyof FeeInputs,
    max: number
  ) => (
    <input
      type="number"
      step="0.01"
      min="0"
      max={max}
      value={currencyFeeInputs[currency]?.[type]?.[field] ?? ''}
      onChange={(e) => setCurrencyFeeField(currency, type, field, e.target.value)}
      placeholder="0"
      className="input font-mono"
      disabled={!canManage}
    />
  );

  return (
    <Layout>
      <div className="space-y-6 animate-fade-in">
        <div className="flex items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-white">Withdrawal settings</h1>
            <p className="text-slate-400 mt-1">
              Fees and daily limits per transfer type, and the threshold for manual admin review.
            </p>
          </div>
          <button
            type="button"
            onClick={load}
            className="btn btn-secondary py-2"
          >
            Refresh
          </button>
        </div>

        <div className="card p-4 text-sm text-slate-400 leading-relaxed">
          <p>
            Every withdrawal is either an
            <span className="text-white font-medium"> external withdrawal</span> (funds leave the
            platform on-chain) or an <span className="text-white font-medium">internal transfer</span>
            {' '}(the address belongs to another platform user; settles instantly, no admin review).
            Each type has its own fee rule per currency and its own daily limits. The visible fee is
            <span className="text-white font-medium"> fee % × amount + fixed fee</span>, but never less
            than <span className="text-white font-medium">min fee</span>; USD values are converted at
            the platform rate. The <span className="text-white font-medium">hidden fee</span> is
            deducted on top of it and is never shown to the user: the client sees only the visible
            components and the resulting net amount. A currency without a rule is free.
          </p>
        </div>

        <div className="card p-6 space-y-4">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h2 className="text-lg font-semibold text-white">Per-currency fees</h2>
              <p className="text-slate-400 text-sm mt-1">
                Applied to every preview and execution. Empty fields count as 0.
              </p>
            </div>
            {settings?.updated_at && (
              <p className="text-xs text-slate-500 whitespace-nowrap">
                Updated {formatTimestamp(settings.updated_at)}
              </p>
            )}
          </div>

          {isLoading ? (
            <div className="flex items-center gap-2 text-slate-400 text-sm">
              <div className="w-4 h-4 border-2 border-indigo-500/30 border-t-indigo-500 rounded-full animate-spin" />
              Loading fees…
            </div>
          ) : (
            <form onSubmit={handleSaveCurrencyFees} className="space-y-4">
              {feeCurrencies.length === 0 ? (
                <p className="text-sm text-amber-400">
                  No fees configured — all withdrawals are currently free for the platform's users.
                </p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-slate-800/50">
                      <tr>
                        <th className="table-header">Currency</th>
                        <th className="table-header">Type</th>
                        <th className="table-header">Fee, %</th>
                        <th className="table-header">Fixed fee, USD</th>
                        <th className="table-header">Min fee, USD</th>
                        <th className="table-header">Hidden fee, %</th>
                        {canManage && <th className="table-header text-right">Actions</th>}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-800">
                      {feeCurrencies.map((currency) =>
                        TRANSFER_TYPES.map(({ type, label }, index) => (
                          <tr key={`${currency}:${type}`} className={index === 0 ? 'border-t border-slate-700' : ''}>
                            {index === 0 && (
                              <td className="table-cell align-top" rowSpan={TRANSFER_TYPES.length}>
                                <span className="text-white font-semibold">{currency}</span>
                              </td>
                            )}
                            <td className="table-cell text-slate-300 whitespace-nowrap">{label}</td>
                            <td className="table-cell">{feeInput(currency, type, 'fee', 50)}</td>
                            <td className="table-cell">{feeInput(currency, type, 'fixed', 100000)}</td>
                            <td className="table-cell">{feeInput(currency, type, 'min', 10000)}</td>
                            <td className="table-cell">{feeInput(currency, type, 'hidden', 50)}</td>
                            {canManage && index === 0 && (
                              <td className="table-cell text-right align-top" rowSpan={TRANSFER_TYPES.length}>
                                <button
                                  type="button"
                                  onClick={() => removeFeeCurrency(currency)}
                                  className="px-3 py-1.5 text-xs font-medium text-red-400 hover:text-red-300 bg-red-500/10 hover:bg-red-500/20 rounded-lg transition-all"
                                >
                                  Remove
                                </button>
                              </td>
                            )}
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              )}

              {allowedWithoutFees.length > 0 && (
                <p className="text-xs text-amber-400">
                  Withdrawal currencies without fees (withdrawals are free):{' '}
                  {allowedWithoutFees.join(', ')}
                </p>
              )}

              {canManage && (
                <div className="flex items-center gap-2">
                  <select
                    value={newFeeCurrency}
                    onChange={(e) => setNewFeeCurrency(e.target.value)}
                    className="input w-40"
                    disabled={addableFeeCurrencies.length === 0}
                  >
                    <option value="">Select currency…</option>
                    {addableFeeCurrencies.map((currency) => (
                      <option key={currency} value={currency}>
                        {currency}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    onClick={addFeeCurrency}
                    disabled={!newFeeCurrency}
                    className="btn btn-secondary py-2 disabled:opacity-50"
                  >
                    Add currency
                  </button>
                </div>
              )}

              <div className="flex flex-col gap-2">
                {error && <p className="text-red-400 text-xs">{error}</p>}
                {currencyFeesBanner && (
                  <p className="text-green-400 text-xs">{currencyFeesBanner}</p>
                )}
                <button
                  type="submit"
                  disabled={!canManage || savingCurrencyFees}
                  className="btn btn-primary w-fit disabled:opacity-50"
                >
                  {savingCurrencyFees ? 'Saving…' : 'Save per-currency fees'}
                </button>
              </div>
            </form>
          )}
        </div>

        <div className="card p-6 space-y-4">
          <div>
            <h2 className="text-lg font-semibold text-white">Daily limits &amp; review threshold</h2>
            <p className="text-slate-400 text-sm mt-1">
              Daily caps are enforced in USD across all currencies, separately for external withdrawals
              and internal transfers. External withdrawals above the review threshold stay in the queue
              until an admin approves them; smaller amounts process automatically after OTP confirmation.
            </p>
          </div>

          {isLoading ? (
            <div className="flex items-center gap-2 text-slate-400 text-sm">
              <div className="w-4 h-4 border-2 border-indigo-500/30 border-t-indigo-500 rounded-full animate-spin" />
              Loading limits…
            </div>
          ) : (
            <form onSubmit={handleSaveLimits} className="space-y-6">
              {TRANSFER_TYPES.map(({ type, label, limitPrefix }) => (
                <div key={type} className="space-y-3">
                  <h3 className="text-sm font-semibold text-white">{label}s — daily limit by level, USD</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {LEVELS.map((level) => {
                      const key = `${limitPrefix}${level}`;
                      return (
                        <div key={key}>
                          <label className="block text-xs font-medium text-slate-400 mb-1">
                            {LEVEL_LABELS[level]}
                          </label>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={limitInputs[key] ?? ''}
                            onChange={(e) => setLimitInput(key, e.target.value)}
                            placeholder={level === 5 ? 'Empty = unlimited' : '0 = not allowed'}
                            className="input font-mono"
                            disabled={!canManage}
                          />
                          {level === 5 && (
                            <p className="text-xs text-slate-500 mt-1">Leave empty for unlimited.</p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}

              <div className="max-w-xs">
                <label className="block text-xs font-medium text-slate-400 mb-1">
                  Manual review above, USD (external only)
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={reviewThresholdInput}
                  onChange={(e) => setReviewThresholdInput(e.target.value)}
                  className="input font-mono"
                  disabled={!canManage}
                />
                <p className="text-xs text-slate-500 mt-1">
                  Amounts at or below this value are auto-processed after OTP.
                </p>
              </div>

              <div className="flex flex-col gap-2">
                {limitsBanner && (
                  <p className="text-green-400 text-xs">{limitsBanner}</p>
                )}
                <button
                  type="submit"
                  disabled={!canManage || savingLimits}
                  className="btn btn-primary w-fit disabled:opacity-50"
                >
                  {savingLimits ? 'Saving…' : 'Save limits'}
                </button>
              </div>
            </form>
          )}
        </div>

        <div className="card p-6 space-y-4">
          <div>
            <h2 className="text-lg font-semibold text-white">Allowed currencies &amp; networks</h2>
            <p className="text-slate-400 text-sm mt-1">
              Users can withdraw only on networks where they have an assigned deposit wallet and the pair is
              enabled below. Options are loaded from the wallet pool.
            </p>
          </div>

          {isLoading ? (
            <p className="text-sm text-slate-400">Loading…</p>
          ) : poolPairs.length === 0 ? (
            <p className="text-sm text-amber-400">No wallet pairs in the pool. Add wallets in Ledger first.</p>
          ) : (
            <form onSubmit={handleSavePairs} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                {poolPairs.map((pair) => {
                  const key = pairKey(pair.currency, pair.network);
                  const checked = selectedPairs.has(key);
                  return (
                    <label
                      key={key}
                      className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition ${
                        checked
                          ? 'border-indigo-500 bg-indigo-500/10'
                          : 'border-slate-700 hover:border-slate-600'
                      } ${!canManage ? 'opacity-60 cursor-not-allowed' : ''}`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        disabled={!canManage}
                        onChange={() => togglePair(pair.currency, pair.network)}
                        className="rounded border-slate-600"
                      />
                      <span className="text-sm text-white font-mono">
                        {formatCurrencyNetworkLabel(pair.currency, pair.network)}
                      </span>
                    </label>
                  );
                })}
              </div>
              <div className="flex flex-col gap-2">
                {pairsBanner && <p className="text-green-400 text-xs">{pairsBanner}</p>}
                <button
                  type="submit"
                  disabled={!canManage || savingPairs}
                  className="btn btn-primary w-fit disabled:opacity-50"
                >
                  {savingPairs ? 'Saving…' : 'Save allowed networks'}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default RatesPage;
